import { useEffect, useRef, useState } from 'react';

/**
 * Measures a sticky page header so tables below it can size to the remaining
 * viewport.
 *
 * Content Tracker already did this with a local ref + ResizeObserver; Plans and
 * Users instead hardcoded `78px`, which silently overflowed the moment anything
 * (a badge row, a filter drawer, a wrapped title) changed the header's height.
 * This hook is that measurement, extracted so every page gets it.
 *
 * Returns 0 until the first observation lands.
 *
 * Usage:
 *   const { headerRef, headerHeight } = useHeaderHeight();
 *   <div ref={headerRef} className="shrink-0"><PageHeader …/></div>
 *   <div style={{ maxHeight: tableScrollMaxHeight(headerHeight) }}>
 */
export function useHeaderHeight<T extends HTMLElement = HTMLDivElement>() {
  const headerRef = useRef<T>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setHeaderHeight(entry.contentRect.height));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { headerRef, headerHeight };
}
